import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { getErrorDetail } from '@/utils/apiError';
import {
  useListPlatformPlansQuery,
  useCreatePlatformPlanMutation,
  useUpdatePlatformPlanMutation,
  useDeletePlatformPlanMutation,
  PlatformPlan,
  CreatePlanRequest,
} from '@/redux/internal-services/platformAdminApi';

const EMPTY_FORM: CreatePlanRequest = {
  name: '',
  description: '',
  price_monthly: null,
  price_annual: null,
  user_limit: null,
  features: [],
  trial_eligible: false,
  trial_duration_days: null,
};

const PlanCard: React.FC<{
  plan: PlatformPlan;
  onEdit: (plan: PlatformPlan) => void;
  onDelete: (plan: PlatformPlan) => void;
  onToggle: (plan: PlatformPlan) => void;
}> = ({ plan, onEdit, onDelete, onToggle }) => (
  <div className={`bg-ui-card border rounded-xl p-6 shadow-sm transition ${plan.is_active === false ? 'opacity-60 border-ui-border2' : 'border-ui-border2'}`}>
    <div className="flex items-start justify-between mb-3">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-ui-text">{plan.name}</h3>
          {plan.is_active === false && (
            <span className="px-2 py-0.5 text-xs bg-ui-subtle text-ui-text2 rounded-full">Inactive</span>
          )}
          {plan.trial_eligible && (
            <span className="px-2 py-0.5 text-xs bg-brand-light text-brand-dark rounded-full">Trial</span>
          )}
        </div>
        <p className="text-sm text-ui-text2 mt-0.5">{plan.description}</p>
      </div>
      <div className="flex gap-2 ml-4">
        <button
          onClick={() => onToggle(plan)}
          className={`text-xs px-2.5 py-1 rounded border transition ${plan.is_active === false ? 'text-green-600 border-green-200 hover:bg-green-50' : 'text-ui-text2 border-ui-border hover:bg-ui-hover'}`}
        >
          {plan.is_active === false ? 'Enable' : 'Disable'}
        </button>
        <button
          onClick={() => onEdit(plan)}
          className="text-xs text-brand-dark border border-brand-light px-2.5 py-1 rounded hover:bg-brand-light/50 transition"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(plan)}
          className="text-xs text-red-500 border border-red-100 px-2.5 py-1 rounded hover:bg-red-50 transition"
        >
          Delete
        </button>
      </div>
    </div>

    <div className="flex flex-wrap gap-4 mb-4">
      <div>
        <p className="text-xs text-ui-text3">Monthly</p>
        <p className="text-lg font-bold text-ui-text">
          {plan.price_monthly != null ? `$${plan.price_monthly}` : 'Custom'}
        </p>
      </div>
      <div>
        <p className="text-xs text-ui-text3">Annual</p>
        <p className="text-lg font-bold text-ui-text">
          {plan.price_annual != null ? `$${plan.price_annual}` : 'Custom'}
        </p>
      </div>
      {plan.user_limit != null && (
        <div>
          <p className="text-xs text-ui-text3">User Limit</p>
          <p className="text-lg font-bold text-ui-text">{plan.user_limit}</p>
        </div>
      )}
      {plan.trial_duration_days != null && plan.trial_eligible && (
        <div>
          <p className="text-xs text-ui-text3">Trial Days</p>
          <p className="text-lg font-bold text-ui-text">{plan.trial_duration_days}</p>
        </div>
      )}
    </div>

    {(plan.features ?? []).length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {plan.features!.map((f) => (
          <span key={f} className="px-2.5 py-0.5 bg-ui-raised text-ui-text2 text-xs rounded-full border border-ui-border2">{f}</span>
        ))}
      </div>
    )}
  </div>
);

type ModalMode = 'create' | 'edit';

const PlanFormModal: React.FC<{
  mode: ModalMode;
  initial: CreatePlanRequest & { plan_id?: string };
  onClose: () => void;
  onSave: (data: CreatePlanRequest & { plan_id?: string }) => void;
  saving: boolean;
  error: string;
}> = ({ mode, initial, onClose, onSave, saving, error }) => {
  const [form, setForm] = useState(initial);
  const [featureInput, setFeatureInput] = useState('');

  const update = (key: keyof typeof form, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const addFeature = () => {
    const f = featureInput.trim();
    if (f && !(form.features ?? []).includes(f)) {
      update('features', [...(form.features ?? []), f]);
    }
    setFeatureInput('');
  };

  const removeFeature = (f: string) =>
    update('features', (form.features ?? []).filter((x) => x !== f));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-ui-card rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-ui-text mb-5">
          {mode === 'create' ? 'Create Plan' : 'Edit Plan'}
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ui-text mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input text-ui-text"
              placeholder="e.g. Professional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ui-text mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              rows={2}
              className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 resize-none bg-ui-input text-ui-text"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ui-text mb-1">Monthly Price ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.price_monthly ?? ''}
                onChange={(e) => update('price_monthly', e.target.value === '' ? null : Number(e.target.value))}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input text-ui-text"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ui-text mb-1">Annual Price ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.price_annual ?? ''}
                onChange={(e) => update('price_annual', e.target.value === '' ? null : Number(e.target.value))}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input text-ui-text"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ui-text mb-1">User Limit (leave blank for unlimited)</label>
            <input
              type="number"
              min="1"
              value={form.user_limit ?? ''}
              onChange={(e) => update('user_limit', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input text-ui-text"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.trial_eligible ?? false}
              onChange={(e) => update('trial_eligible', e.target.checked)}
              className="w-4 h-4 accent-brand-dark"
            />
            <span className="text-sm text-ui-text">Trial eligible</span>
          </label>

          {form.trial_eligible && (
            <div>
              <label className="block text-sm font-medium text-ui-text mb-1">Trial Duration (days)</label>
              <input
                type="number"
                min="1"
                value={form.trial_duration_days ?? ''}
                onChange={(e) => update('trial_duration_days', e.target.value === '' ? null : Number(e.target.value))}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input text-ui-text"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ui-text mb-1">Features</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }}
                className="flex-1 border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input text-ui-text"
                placeholder="Add a feature..."
              />
              <button
                type="button"
                onClick={addFeature}
                className="px-3 py-2 bg-brand-dark text-white text-sm rounded-lg hover:bg-[#3d2aa0] transition"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(form.features ?? []).map((f) => (
                <span key={f} className="flex items-center gap-1 px-2.5 py-0.5 bg-brand-light text-brand-dark text-xs rounded-full">
                  {f}
                  <button type="button" onClick={() => removeFeature(f)} className="text-brand-dark/60 hover:text-brand-dark">×</button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="mt-4 text-sm text-red-500">{error}</p>}

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 border border-ui-border text-ui-text py-2 rounded-lg text-sm hover:bg-ui-hover transition"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={saving}
            className="flex-1 bg-brand-dark text-white py-2 rounded-lg text-sm font-medium hover:bg-[#3d2aa0] transition disabled:opacity-50"
          >
            {saving ? 'Saving...' : mode === 'create' ? 'Create Plan' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useListPlatformPlansQuery();
  const [createPlan, { isLoading: creating }] = useCreatePlatformPlanMutation();
  const [updatePlan, { isLoading: updating }] = useUpdatePlatformPlanMutation();
  const [deletePlan] = useDeletePlatformPlanMutation();

  const plans: PlatformPlan[] = data?.data ?? [];

  const [modal, setModal] = useState<null | { mode: ModalMode; plan: CreatePlanRequest & { plan_id?: string } }>(null);
  const [modalError, setModalError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<PlatformPlan | null>(null);
  const [globalMsg, setGlobalMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const openCreate = () => { setModal({ mode: 'create', plan: { ...EMPTY_FORM } }); setModalError(''); };
  const openEdit = (plan: PlatformPlan) => {
    setModal({
      mode: 'edit',
      plan: {
        plan_id: plan.plan_id,
        name: plan.name,
        description: plan.description,
        price_monthly: plan.price_monthly,
        price_annual: plan.price_annual,
        user_limit: plan.user_limit ?? null,
        features: plan.features ?? [],
        trial_eligible: plan.trial_eligible ?? false,
        trial_duration_days: plan.trial_duration_days ?? null,
      },
    });
    setModalError('');
  };

  const handleSave = async (formData: CreatePlanRequest & { plan_id?: string }) => {
    setModalError('');
    if (!formData.name.trim()) { setModalError('Plan name is required.'); return; }
    try {
      if (modal?.mode === 'create') {
        await createPlan(formData).unwrap();
        setGlobalMsg({ type: 'success', text: 'Plan created.' });
      } else if (formData.plan_id) {
        const { plan_id, ...rest } = formData;
        await updatePlan({ planId: plan_id!, data: rest }).unwrap();
        setGlobalMsg({ type: 'success', text: 'Plan updated.' });
      }
      setModal(null);
      refetch();
    } catch (err: unknown) {
      setModalError(getErrorDetail(err) ?? 'Failed to save plan.');
    }
  };

  const handleToggle = async (plan: PlatformPlan) => {
    try {
      await updatePlan({ planId: plan.plan_id, data: { is_active: plan.is_active === false ? true : false } }).unwrap();
      refetch();
    } catch { /* silently fail */ }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deletePlan(deleteConfirm.plan_id).unwrap();
      setGlobalMsg({ type: 'success', text: 'Plan deleted.' });
      setDeleteConfirm(null);
      refetch();
    } catch (err: unknown) {
      setGlobalMsg({ type: 'error', text: getErrorDetail(err) ?? 'Failed to delete plan.' });
      setDeleteConfirm(null);
    }
  };

  return (
    <PageShell
      maxWidth="max-w-5xl"
      header={
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ui-text">Subscription Plans</h1>
            <p className="text-sm text-ui-text2 mt-1">Manage available plans and pricing for tenants.</p>
          </div>
          <button
            onClick={openCreate}
            className="self-start sm:self-auto flex items-center gap-2 bg-brand-dark text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3d2aa0] transition"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Plan
          </button>
        </div>
      }
    >

      {globalMsg && (
        <p className={`mb-6 text-sm ${globalMsg.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{globalMsg.text}</p>
      )}

      {isLoading ? (
        <div className="py-20 text-center text-ui-text3 text-sm">Loading plans...</div>
      ) : plans.length === 0 ? (
        <div className="border border-dashed border-ui-border rounded-xl py-20 text-center">
          <p className="text-ui-text3 text-sm mb-3">{t('platform.noPlansYet')}</p>
          <button onClick={openCreate} className="text-sm text-brand-dark font-medium hover:underline">
            {t('platform.createFirstPlan')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => (
            <PlanCard
              key={plan.plan_id}
              plan={plan}
              onEdit={openEdit}
              onDelete={(p) => setDeleteConfirm(p)}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}

      {/* Plan form modal */}
      {modal && (
        <PlanFormModal
          mode={modal.mode}
          initial={modal.plan}
          onClose={() => setModal(null)}
          onSave={handleSave}
          saving={creating || updating}
          error={modalError}
        />
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-ui-card rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold text-ui-text mb-2">Delete Plan</h3>
            <p className="text-sm text-ui-text2 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-ui-border text-ui-text py-2 rounded-lg text-sm hover:bg-ui-hover transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 bg-danger text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default PlansPage;
